import { Array, pipe, Record } from 'effect'

import { getBuiltInModels } from '../../../constant/builtInModels.ts'
import { PRESET_MODELS } from '../../../constant/presetModels.ts'
import type { CoreModel } from '../../../schemas/model/model.ts'
import type { ModelProperty } from '../../../schemas/model/modelProperty.ts'

type PropertyMapper = Record<string, ModelProperty>
type Mapper = Record<
  string,
  {
    model: CoreModel
    propertyMapper: PropertyMapper
  }
>

export class ModelSearcher {
  public readonly models: CoreModel[]
  private readonly mapper: Mapper

  public constructor(models: CoreModel[], userGroupCategoryNames?: string[]) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    // 組み込みモデルのうち2つを動的に生成するために、userGroupCategoryNamesを引数にとっている
    // この実装やめたいので、普通にモデル側に以下相当のモデルを永続化して持ちたい
    this.models = [...models, ...getBuiltInModels(userGroupCategoryNames ?? [])]
    this.mapper = pipe(
      this.models,
      Array.groupBy((x) => x.name),
      Record.map(([model]) => ({
        model, // 必ず1つのみ存在する
        propertyMapper: Record.fromIterableBy(Array.reverse(model.properties), (x) => x.name),
      })),
    )
  }

  public searchModel(modelName: string | undefined): CoreModel | undefined {
    if (modelName === undefined) {
      return undefined
    }
    return this.mapper[modelName]?.model
  }

  // deprecated. withModel側を使うのを推奨

  public searchProperty(modelName: string | undefined, propertyName: string | undefined): ModelProperty | undefined {
    if (modelName === undefined || propertyName === undefined) {
      return undefined
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: idというプロパティは現状存在しないが、salescore_usersだけは無理やり生やしている。
    if (modelName === 'salescore_users' && propertyName === 'id') {
      return PRESET_MODELS.USER.ID_PROPERTY
    }
    if (modelName === 'salescore_user_groups' && propertyName === 'id') {
      return PRESET_MODELS.USER_GROUP.ID_PROPERTY
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: 2022/09/26 移行の関連で、全てのモデルについてidプロパティを生やしてしまうことにした。これで問題ないか調査
    if (propertyName === 'id') {
      return PRESET_MODELS.USER_GROUP.ID_PROPERTY
    }
    const propertyMapper = this.mapper[modelName]?.propertyMapper
    if (propertyMapper === undefined) {
      return
    }
    return propertyMapper[propertyName]
  }

  public searchPropertyWithModel(
    modelName: string | undefined,
    propertyName: string | undefined,
  ): { model: CoreModel; property: ModelProperty } | undefined {
    if (modelName === undefined || propertyName === undefined) {
      return undefined
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: idというプロパティが存在しないが、salescore_usersだけは無理やり生やしている。
    if (modelName === 'salescore_users' && propertyName === 'id') {
      return {
        model: PRESET_MODELS.USER.MODEL,
        property: PRESET_MODELS.USER.ID_PROPERTY,
      }
    }
    if (modelName === 'salescore_user_groups' && propertyName === 'id') {
      return {
        model: PRESET_MODELS.USER_GROUP.MODEL,
        property: PRESET_MODELS.USER_GROUP.ID_PROPERTY,
      }
    }
    const model = this.mapper[modelName]
    if (model === undefined) {
      return
    }
    const property = model.propertyMapper[propertyName]
    if (property === undefined) {
      return
    }
    return {
      model: model.model,
      property,
    }
  }
}
